import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const badgeVariants = cva(
  "inline-flex items-center rounded-md border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
  {
    variants: {
      variant: {
        default:
          "border-transparent bg-primary text-primary-foreground shadow hover:bg-primary/80",
        secondary:
          "border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80",
        destructive:
          "border-transparent bg-destructive text-destructive-foreground shadow hover:bg-destructive/80",
        outline: "text-foreground",
        // Status badge variants (matching orders page)
        status:
          "rounded-full px-[10px] py-[5px] border-transparent",
        // Channel-specific variants
        email:
          "rounded-full px-[10px] py-[5px] border-transparent bg-blue-500/20 text-blue-600 hover:bg-blue-500/20",
        sms:
          "rounded-full px-[10px] py-[5px] border-transparent bg-green-500/20 text-green-600 hover:bg-green-500/20",
        whatsapp:
          "rounded-full px-[10px] py-[5px] border-transparent bg-emerald-500/20 text-emerald-600 hover:bg-emerald-500/20",
        // Special dispatching variant (used by orders)
        dispatching:
          "rounded-full px-[10px] py-[5px] border-transparent bg-orange-500/20 text-orange-600 hover:bg-orange-500/20",
        // Order status variants
        new:
          "rounded-full px-[10px] py-[5px] border-transparent bg-blue-500/20 text-blue-600 hover:bg-blue-500/20",
        delivered:
          "rounded-full px-[10px] py-[5px] border-transparent bg-emerald-500/20 text-emerald-600 hover:bg-emerald-500/20",
        cancelled:
          "rounded-full px-[10px] py-[5px] border-transparent bg-gray-500/20 text-gray-600 hover:bg-gray-500/20",
        picked_up:
          "rounded-full px-[10px] py-[5px] border-transparent bg-indigo-500/20 text-indigo-600 hover:bg-indigo-500/20",
        out_for_delivery:
          "rounded-full px-[10px] py-[5px] border-transparent bg-purple-500/20 text-purple-600 hover:bg-purple-500/20",
        rto_initiated:
          "rounded-full px-[10px] py-[5px] border-transparent bg-amber-500/20 text-amber-600 hover:bg-amber-500/20",
        on_hold:
          "rounded-full px-[10px] py-[5px] border-transparent bg-yellow-500/20 text-yellow-600 hover:bg-yellow-500/20",
        // Generic status variants (for all status indicators)
        success:
          "rounded-full px-[10px] py-[5px] border-transparent bg-green-500/20 text-green-600 hover:bg-green-500/20",
        warning:
          "rounded-full px-[10px] py-[5px] border-transparent bg-yellow-500/20 text-yellow-600 hover:bg-yellow-500/20",
        error:
          "rounded-full px-[10px] py-[5px] border-transparent bg-red-500/20 text-red-600 hover:bg-red-500/20",
        info:
          "rounded-full px-[10px] py-[5px] border-transparent bg-blue-500/20 text-blue-600 hover:bg-blue-500/20",
        neutral:
          "rounded-full px-[10px] py-[5px] border-transparent bg-gray-500/20 text-gray-600 hover:bg-gray-500/20",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {}

function Badge({ className, variant, ...props }: BadgeProps) {
  return (
    <div className={cn(badgeVariants({ variant }), className)} {...props} />
  );
}

export { Badge, badgeVariants };
